import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
// @ts-ignore
import meta from '../../../meta.json';

import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { getStorage } from 'helpers/sessionContext';

const BANNER_STORAGE_KEY = 'cincel-banner-dismissed-at';
const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

const isBannerDismissed = (): boolean => {
  try {
    const dismissedAt = getStorage().getItem(BANNER_STORAGE_KEY);
    if (!dismissedAt) return false;
    const elapsed = Date.now() - parseInt(dismissedAt, 10);
    if (elapsed < TWO_HOURS_MS) return true;
    getStorage().removeItem(BANNER_STORAGE_KEY);
    return false;
  } catch {
    return false;
  }
};

const versionTag = `${(meta as any).tag}+${(meta as any).distance}`;

export const PoweredByCincel: React.FC<{ bannerContent?: React.ReactNode }> = ({
  bannerContent,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(!isBannerDismissed());

  const handleDismiss = () => {
    try {
      getStorage().setItem(BANNER_STORAGE_KEY, String(Date.now()));
    } catch { /* noop */ }
    setIsOpen(false);
  };

  return (
    <div id="powered-by-cincel" className="fixed bottom-2 right-6 z-50 flex flex-col items-end pointer-events-none">
      <motion.div
        layout
        initial={false}
        className={classNames(
          'pointer-events-auto rounded-lg shadow-lg border transition-colors duration-300 overflow-hidden',
          isOpen
            ? 'border-transparent text-white'
            : 'backdrop-blur-sm bg-white/60 border-white/40 hover:bg-white/80 shadow-sm',
        )}
        style={isOpen ? { background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' } : undefined}
      >
        <AnimatePresence exitBeforeEnter initial={false}>
          {isOpen && bannerContent ? (
            <motion.div
              key="banner"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative pr-8"
            >
              {/* Close button - right side */}
              <button
                onClick={handleDismiss}
                className="absolute top-2 right-2 text-white/40 hover:text-white transition-colors p-0.5"
                aria-label={t(translations.COMMON.CLOSE)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Banner message - right aligned */}
              <div className="px-4 pt-2.5 pb-1.5 text-sm text-right">
                {bannerContent}
              </div>

              {/* Version footer inside banner - right aligned */}
              <div className="px-4 pb-2 pt-0.5 flex items-center justify-end border-t border-white/10">
                <span className="text-[10px] text-white/40">
                  Powered by <strong className="text-white/60">Cincel</strong>
                </span>
                <span className="mx-1.5 h-2.5 w-px bg-white/20"></span>
                <span className="text-[10px] text-white/40 font-medium">
                  {versionTag}
                </span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="footer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Link to="/about" className="block px-3 py-1.5 text-decoration-none">
                <p className="text-xs text-gray-500 flex items-center m-0">
                  <span>Powered by </span>
                  <strong className="ml-1">Cincel</strong>
                  <span className="mx-2 h-3 w-px bg-gray-400 opacity-50"></span>
                  <span className="text-[10px] opacity-80 font-medium">
                    {versionTag}
                  </span>
                </p>
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};
